/**
 * Joins one inbound request to the board's scheduling pools — the pure half of the Requests-tab
 * scheduling connection (rendering in RequestSchedulingConnection.tsx).
 *
 * The Requests tab and the Scheduling tab used to be two worlds: a candidate writing "please
 * propose times" was read in one tab, and scheduled — if the recruiter remembered — in another.
 * This resolver answers "where does this candidate sit on MY scheduling tab", so the request card
 * can render the same candidate card the Scheduling tab would, or say exactly why there is none.
 *
 * A miss carries a REASON, because "no card" has four very different fixes: a cold sender has no
 * Lever record at all; a role can be missing from the board (someone else's posting, or the role
 * filter of an older payload); the candidate can sit in a stage the scheduling pools do not draw
 * from (the fix is moving them in Lever); or they are in a pool-feeding stage but absent from the
 * pool — which on this board means the work is already done or parked (panel booked, snoozed).
 */
import type {
  FirstInterviewCandidate,
  InboundRequest,
  RoleBoard,
  SchedulingCandidate,
  StageRef,
} from '../../types/taActionBoard';

export type SchedulingMatch =
  /** In the 📅 Case pool — the SchedulingCard renders inline, fully armed. */
  | { kind: 'case'; role: RoleBoard; candidate: SchedulingCandidate }
  /** In the 🏁 Final-rounds pool — the FinalRoundCard renders inline. */
  | { kind: 'final'; role: RoleBoard; candidate: SchedulingCandidate }
  /** In the 📨 First-interviews pool — invites are a per-role batch, so this points at the tab. */
  | { kind: 'first'; role: RoleBoard; candidate: FirstInterviewCandidate }
  | {
      kind: 'none';
      reason: 'no_lever' | 'role_not_on_board' | 'stage_not_interview' | 'not_in_pool';
      role: RoleBoard | null;
    };

/**
 * The intents where scheduling is the ANSWER — a miss on these renders the warning. Mirrors the
 * proxy's SCHEDULING_CATEGORIES (inboundScheduling.js). `scheduling_confirm` is deliberately
 * absent: a confirmed slot means the scheduling already happened, and warning "not in the
 * scheduling pool" about it would be noise on every settled thread.
 */
const SCHEDULING_INTENTS = new Set<InboundRequest['category']>([
  'reschedule',
  'availability_window',
  'technical_issue',
  'scheduling_request',
]);

export const isSchedulingIntent = (item: InboundRequest) => SCHEDULING_INTENTS.has(item.category);

/** Where this request's candidate sits in the board's scheduling pools, or why nowhere. */
export function resolveSchedulingMatch(
  item: InboundRequest,
  roles: RoleBoard[],
  stages: StageRef[]
): SchedulingMatch {
  if (!item.leverMatched || !item.opportunityId) {
    return { kind: 'none', reason: 'no_lever', role: null };
  }
  const oppId = item.opportunityId;

  /**
   * The request names a posting; the pools are searched everywhere anyway. The role join can be
   * wrong or missing (`roleUnknownReason`), while an opportunityId in a pool is exact — so a pool
   * hit on another role wins over "not found under the named one".
   */
  const searchOrder = [
    ...roles.filter((r) => r.postingId === item.postingId),
    ...roles.filter((r) => r.postingId !== item.postingId),
  ];
  for (const role of searchOrder) {
    const inCase = role.scheduling.candidates.find((c) => c.opportunityId === oppId);
    if (inCase) return { kind: 'case', role, candidate: inCase };
    const inFinal = role.finalRounds?.candidates?.find((c) => c.opportunityId === oppId);
    if (inFinal) return { kind: 'final', role, candidate: inFinal };
    const inFirst = role.firstInterviews?.candidates?.find((c) => c.opportunityId === oppId);
    if (inFirst) return { kind: 'first', role, candidate: inFirst };
  }

  const role = roles.find((r) => r.postingId === item.postingId) || null;
  if (!role) return { kind: 'none', reason: 'role_not_on_board', role: null };

  /**
   * In a stage the pools draw from but not in any pool: on this board that means the scheduling
   * work is done or parked (case panel booked, snoozed, invite already sent). Matched by NAME
   * because the request carries only `stageName`; an unknown name falls to `not_in_pool` — the
   * calmer message — rather than telling a recruiter to move a candidate we cannot place.
   */
  const stage = item.stageName ? stages.find((s) => s.name === item.stageName) : null;
  if (stage && !stage.interviewish) {
    return { kind: 'none', reason: 'stage_not_interview', role };
  }
  return { kind: 'none', reason: 'not_in_pool', role };
}
